import React from "react";

import PrimaryButton from "~/components/buttons/primaryButton";
import SecondaryButton from "~/components/buttons/secondaryButton";
import type { ConferenceInvitePayload } from "~/services/calling/types";

type ConferenceInviteModalProps = {
  invite: ConferenceInvitePayload | null | undefined;
  onJoin: (room: string) => void | Promise<void>;
  onDismiss: () => void;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

export default function ConferenceInviteModal({
  invite,
  onJoin,
  onDismiss,
}: ConferenceInviteModalProps) {
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!invite) {
      setLoading(false);
    }
  }, [invite]);

  if (!invite) return null;

  const handleJoin = async () => {
    setLoading(true);
    try {
      await onJoin(invite.room);
      onDismiss();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close invite"
        onClick={onDismiss}
        className="absolute inset-0 bg-black/60"
      />

      <div className="relative flex min-h-full items-center justify-center p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Conference invite"
          className={cn(
            "w-96 max-w-[94vw]",
            "rounded-[18px] border border-[#2C313B]",
            "bg-[#0D111B] text-white",
            "shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
          )}
        >
          <div className="px-6 pt-6">
            <div className="text-[18px] font-semibold">Conference Invite</div>
            <div className="mt-2 text-[13px] text-[#99A1AA]">
              You have been invited to join room {invite.room}.
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#2C313B] px-6 py-4">
            <SecondaryButton className="h-10 flex-1" onClick={onDismiss}>
              Dismiss
            </SecondaryButton>
            <PrimaryButton
              className="h-10 flex-1"
              onClick={handleJoin}
              loading={loading}
            >
              Join Room
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
